import { useEffect, useState } from 'react';
import { InboxOutlined } from '@ant-design/icons';
import { Button, Card, Drawer, Select, Space, Table, Tag, Typography, Upload, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { UploadFile } from 'antd/es/upload/interface';
import { useNavigate, useParams } from 'react-router-dom';

import LayoutWithMenu from 'components/Layout/LayoutWithMenu';
import { post } from 'core/fetch';

const { Title } = Typography;
const { Dragger } = Upload;

interface ApiResponse<T> {
  valid: boolean;
  errorMessage?: string;
  data?: T;
}

interface ImportFileMeta {
  name: string;
  fileKey: string;
  url: string;
  mimetype: string;
  isPublic: boolean;
}

type ActionType = 'CREATE_ONLY' | 'UPDATE_ONLY' | 'CREATE_UPDATE';
type ImportStatus = 'COMPARE' | 'DONE';

interface StudentImportRow {
  id: number;
  import_file: ImportFileMeta | null;
  action_type: ActionType;
  status: ImportStatus;
  total_record: number;
  created_record: number;
  updated_record: number;
  skipped_record: number;
  _created_at?: string;
}

const ACTION_TYPE_LABELS: Record<ActionType, string> = {
  CREATE_ONLY: 'Зөвхөн шинээр нэмэх',
  UPDATE_ONLY: 'Зөвхөн шинэчлэх',
  CREATE_UPDATE: 'Нэмэх, шинэчлэх хоёулаа',
};

const STATUS_TAGS: Record<ImportStatus, { color: string; label: string }> = {
  COMPARE: { color: 'gold', label: 'Харьцуулах бэлэн' },
  DONE: { color: 'green', label: 'Дууссан' },
};

const StudentImportList = () => {
  const navigate = useNavigate();
  const { appKey } = useParams<{ appKey: string }>();

  const [rows, setRows] = useState<StudentImportRow[]>([]);
  const [loading, setLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [file, setFile] = useState<UploadFile | null>(null);
  const [actionType, setActionType] = useState<ActionType>('CREATE_UPDATE');
  const [submitting, setSubmitting] = useState(false);

  const loadRows = () => {
    setLoading(true);
    post<StudentImportRow[]>('/student_import/search', { $sort: { id: 'desc' } })
      .then(setRows)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadRows();
  }, []);

  const resetModal = () => {
    setModalOpen(false);
    setFile(null);
    setActionType('CREATE_UPDATE');
  };

  const handleImport = async () => {
    if (!file) {
      message.warning('CSV файл сонгоно уу.');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('files', file as unknown as Blob);

      const uploaded = await post<ImportFileMeta>('/student_import/storage/import_file', formData, {
        'Content-Type': 'multipart/form-data',
      });

      // The upload middleware decodes non-ASCII filenames as Latin-1, which
      // mangles Cyrillic names ("Жагсаалт.csv" → "ÐÐ°Ð³ÑÐ°Ð°Ð»Ñ.csv"). The
      // browser's own File.name is always correctly decoded, so use that
      // instead of trusting the server's echoed name.
      const response = await post<ApiResponse<{ id: number }>>('/custom/student-import/create', {
        import_file: { ...uploaded, name: file.name },
        action_type: actionType,
      });

      if (!response.valid || !response.data) {
        message.error(response.errorMessage ?? 'Импорт хийхэд алдаа гарлаа.');
        return;
      }

      resetModal();
      navigate(`/apps/${appKey}/import_students/compare/${response.data.id}`);
    } catch (err: any) {
      message.error(err?.error ?? err?.errorMessage ?? err?.message ?? 'Импорт хийхэд алдаа гарлаа.');
    } finally {
      setSubmitting(false);
    }
  };

  const columns: ColumnsType<StudentImportRow> = [
    {
      title: 'Огноо',
      dataIndex: '_created_at',
      width: 160,
      render: (value: string) => (value ? new Date(value).toLocaleString('mn-MN') : '—'),
    },
    {
      title: 'Файл',
      dataIndex: ['import_file', 'name'],
      render: (_, row) => row.import_file?.name ?? '—',
    },
    {
      title: 'Үйлдлийн төрөл',
      dataIndex: 'action_type',
      render: (value: ActionType) => ACTION_TYPE_LABELS[value],
    },
    {
      title: 'Төлөв',
      dataIndex: 'status',
      render: (value: ImportStatus) => (
        <Tag color={STATUS_TAGS[value].color}>{STATUS_TAGS[value].label}</Tag>
      ),
    },
    { title: 'Нийт', dataIndex: 'total_record', align: 'right' },
    { title: 'Шинээр нэмэгдэх', dataIndex: 'created_record', align: 'right' },
    { title: 'Шинэчлэгдэх', dataIndex: 'updated_record', align: 'right' },
    { title: 'Алгасагдах', dataIndex: 'skipped_record', align: 'right' },
  ];

  return (
    <LayoutWithMenu className="px-3 lg:px-5">
      <Card>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16,
          }}
        >
          <Title level={4} style={{ margin: 0 }}>
            Сурагч импортлох
          </Title>
          <Button type="primary" onClick={() => setModalOpen(true)}>
            Шинэ импорт
          </Button>
        </div>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={rows}
          loading={loading}
          pagination={false}
          onRow={(row) => ({
            style: { cursor: 'pointer' },
            onClick: () => navigate(`/apps/${appKey}/import_students/compare/${row.id}`),
          })}
        />
      </Card>

      <Drawer
        title="Шинэ импорт"
        open={modalOpen}
        onClose={resetModal}
        placement="right"
        width={640}
        destroyOnClose
        extra={
          <Space>
            <Button onClick={resetModal}>Болих</Button>
            <Button type="primary" onClick={handleImport} loading={submitting}>
              Импортлох
            </Button>
          </Space>
        }
      >
        <div
          style={{
            marginBottom: 16,
            padding: '10px 12px',
            borderRadius: 6,
            background: '#f5f5f5',
            color: 'rgba(0, 0, 0, 0.45)',
            fontSize: 13,
          }}
        >
          CSV файлын баганы нэрс (эхний мөр header байх ёстой):
          <br />
          <span style={{ fontFamily: 'monospace' }}>
            register_no, last_name, first_name, family_name, gender, status, class_name,
            home_address, phone, private_email
          </span>
        </div>

        <Dragger
          maxCount={1}
          accept=".csv"
          height={150}
          fileList={file ? [file] : []}
          beforeUpload={(selected) => {
            setFile(selected);
            return false;
          }}
          onRemove={() => setFile(null)}
        >
          <p className="ant-upload-drag-icon" style={{ marginBottom: 4, fontSize: 24 }}>
            <InboxOutlined />
          </p>
          <p className="ant-upload-text" style={{ fontSize: 13 }}>
            CSV файлаа энд чирж оруулна уу
          </p>
        </Dragger>

        <div style={{ marginTop: 16 }}>
          <Select<ActionType>
            value={actionType}
            onChange={setActionType}
            style={{ width: '100%' }}
            options={[
              { value: 'CREATE_ONLY', label: 'Зөвхөн шинээр нэмэх' },
              { value: 'UPDATE_ONLY', label: 'Зөвхөн шинэчлэх' },
              { value: 'CREATE_UPDATE', label: 'Нэмэх, шинэчлэх хоёулаа' },
            ]}
          />
        </div>
      </Drawer>
    </LayoutWithMenu>
  );
};

export default StudentImportList;
